import { useMutation, useQuery } from '@apollo/client/react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { filter, find, map } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';

import OverwatchModal from '@/components/OverwatchModal';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  type DeliveryAndCollectionItem,
  CreateDeliveryAndCollectionRegisterDocument,
  GetCompanyResourcesDocument,
  CompanyResourceResourceTypeEnum,
  type DeliveryAndCollectionRegisterCreate,
  GetDeliveryItemStatesDocument,
} from '@/graphql/generated/graphql';
import { arrayLength } from '@/utils/helperFunctions';
import { displayNotification } from '@/utils/notifications';

interface DeliveryModalProps {
  handleClose: () => void;
}

interface FormErrors {
  [key: number]: {
    [K in keyof DeliveryAndCollectionItem]?: boolean;
  };
}

const INITIAL_ITEM: DeliveryAndCollectionItem = {
  notes: '',
  quantity: '1',
  supplier: '',
  companyResourceId: '',
  delivererName: '',
  receiverName: '',
};

const REQUIRED_FIELDS: (keyof DeliveryAndCollectionItem)[] = [
  'quantity',
  'supplier',
  'companyResourceId',
  'delivererName',
  'receiverName',
];

const DeliveryModal: React.FC<DeliveryModalProps> = ({ handleClose }) => {
  const { selectedReport } = useContext(ReportContext);

  const [items, setItems] = useState<DeliveryAndCollectionItem[]>([INITIAL_ITEM]);
  const [errors, setErrors] = useState<FormErrors>({});

  const [createDeliveryRegister, { loading: isSaving }] = useMutation(CreateDeliveryAndCollectionRegisterDocument, {
    onCompleted: () => {
      displayNotification({
        text: 'Delivery and Collection Register created successfully',
        type: 'success',
      });
      handleClose();
    },
    onError: (error) => {
      displayNotification({ text: error.message, type: 'error' });
    },
    refetchQueries: ['getReportForSelectedDate', 'getDeliveryRegisters'],
  });

  const { data, loading: isLoadingEquipments } = useQuery(GetCompanyResourcesDocument, {
    fetchPolicy: 'cache-and-network',
  });

  const equipmentOptions = useMemo(
    () =>
      map(
        filter(
          data?.getCompanyResources || [],
          (opt) => opt.resourceType === CompanyResourceResourceTypeEnum.Equipment,
        ),
        (option) => ({
          id: option.id,
          name: option.name,
        }),
      ) || [],
    [data],
  );

  const validateItem = useCallback((item: DeliveryAndCollectionItem, index: number) => {
    const itemErrors: { [K in keyof DeliveryAndCollectionItem]?: boolean } = {};

    REQUIRED_FIELDS.forEach((field) => {
      if (!item[field]?.toString().trim()) {
        itemErrors[field] = true;
      }
    });

    setErrors((prev) => ({
      ...prev,
      [index]: itemErrors,
    }));

    return Object.keys(itemErrors).length === 0;
  }, []);

  const validateAllItems = useCallback(() => {
    return items.every((item, index) => validateItem(item, index));
  }, [items, validateItem]);

  const handleAddItem = useCallback(() => {
    setItems((prev) => [...prev, { ...INITIAL_ITEM }]);
  }, []);

  const handleDeleteItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      // Reindex remaining errors
      const reindexedErrors: FormErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const numKey = parseInt(key);
        if (numKey > index) {
          reindexedErrors[numKey - 1] = newErrors[numKey];
        } else if (numKey < index) {
          reindexedErrors[numKey] = newErrors[numKey];
        }
      });
      return reindexedErrors;
    });
  }, []);

  const handleItemChange = useCallback(
    (index: number, field: keyof DeliveryAndCollectionItem, value: string) => {
      setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));

      // Clear error for this field when user starts typing
      if (errors[index]?.[field]) {
        setErrors((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            [field]: false,
          },
        }));
      }
    },
    [errors],
  );

  const handleSave = useCallback(() => {
    if (!validateAllItems()) {
      displayNotification({
        text: 'Please fill in all required fields',
        type: 'error',
      });
      return;
    }

    createDeliveryRegister({
      variables: {
        deliveryAndCollectionRegister: {
          reportId: selectedReport?.id || '',
          items,
        } as DeliveryAndCollectionRegisterCreate,
      },
      update(cache, { data }) {
        const createdStates = data?.createDeliveryAndCollectionRegister ?? [];
        const reportId = selectedReport?.id;

        if (!reportId || arrayLength(createdStates) === 0) return;

        const existingItems =
          cache.readQuery({
            query: GetDeliveryItemStatesDocument,
            variables: { reportId },
          })?.getDeliveryItemStates ?? [];
        const existingIds = new Set(map(existingItems, 'id'));

        cache.writeQuery({
          query: GetDeliveryItemStatesDocument,
          variables: { reportId },
          data: {
            getDeliveryItemStates: [...existingItems, ...filter(createdStates, (item) => !existingIds.has(item.id))],
          },
        });
      },
    });
  }, [items, selectedReport?.id, createDeliveryRegister, validateAllItems]);

  const canAddItem = useMemo(() => items.length === 0 || validateAllItems(), [items, validateAllItems]);

  const canSave = useMemo(() => items.length > 0 && validateAllItems(), [items, validateAllItems]);

  const getFieldError = (index: number, field: keyof DeliveryAndCollectionItem) => {
    return errors[index]?.[field] || false;
  };

  const getFieldHelperText = (index: number, field: keyof DeliveryAndCollectionItem, label: string) => {
    return getFieldError(index, field) ? `${label} is required` : '';
  };

  return (
    <OverwatchModal
      subtitle="New Delivery / Collection Entry"
      primaryButtonText="Save"
      primaryButtonAction={handleSave}
      disablePrimary={!canSave || isSaving}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
      handleClose={handleClose}
      size="md"
    >
      <Box sx={{ maxHeight: '70vh', overflowY: 'auto', px: 1 }}>
        {items.map((item, index) => (
          <Card
            key={index}
            variant="outlined"
            sx={{
              mb: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 2,
                borderColor: 'primary.main',
              },
            }}
          >
            <CardContent sx={{ pb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Item {index + 1}
                  {Object.keys(errors[index] || {}).length > 0 && (
                    <Chip label="Incomplete" color="error" size="small" variant="outlined" />
                  )}
                </Typography>
                {items.length > 1 && (
                  <IconButton
                    onClick={() => handleDeleteItem(index)}
                    color="error"
                    size="small"
                    sx={{
                      '&:hover': {
                        backgroundColor: 'error.light',
                        '& .MuiSvgIcon-root': { color: 'white' },
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                {/* Equipment Selection */}
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Autocomplete
                    options={equipmentOptions}
                    getOptionLabel={(option) => option.name || ''}
                    loading={isLoadingEquipments}
                    value={find(equipmentOptions, { id: item.companyResourceId }) || null}
                    onChange={(_, newValue) => handleItemChange(index, 'companyResourceId', newValue?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Equipment *"
                        size="small"
                        fullWidth
                        error={getFieldError(index, 'companyResourceId')}
                        helperText={getFieldHelperText(index, 'companyResourceId', 'Equipment')}
                      />
                    )}
                  />
                </Grid>

                {/* Quantity */}
                <Grid
                  size={{
                    xs: 12,
                    md: 2,
                  }}
                >
                  <TextField
                    label="Quantity *"
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    size="small"
                    fullWidth
                    error={getFieldError(index, 'quantity')}
                    helperText={getFieldHelperText(index, 'quantity', 'Quantity')}
                    slotProps={{
                      htmlInput: { min: 1 },
                    }}
                  />
                </Grid>

                {/* Supplier */}
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <TextField
                    label="Supplier *"
                    value={item.supplier}
                    onChange={(e) => handleItemChange(index, 'supplier', e.target.value)}
                    size="small"
                    fullWidth
                    error={getFieldError(index, 'supplier')}
                    helperText={getFieldHelperText(index, 'supplier', 'Supplier')}
                  />
                </Grid>

                {/* Deliverer Name */}
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <TextField
                    label="Deliverer Name *"
                    value={item.delivererName}
                    onChange={(e) => handleItemChange(index, 'delivererName', e.target.value)}
                    size="small"
                    fullWidth
                    error={getFieldError(index, 'delivererName')}
                    helperText={getFieldHelperText(index, 'delivererName', 'Deliverer')}
                  />
                </Grid>

                {/* Receiver Name */}
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <TextField
                    label="Receiver Name *"
                    value={item.receiverName}
                    onChange={(e) => handleItemChange(index, 'receiverName', e.target.value)}
                    size="small"
                    fullWidth
                    error={getFieldError(index, 'receiverName')}
                    helperText={getFieldHelperText(index, 'receiverName', 'Receiver')}
                  />
                </Grid>

                {/* Notes */}
                <Grid size={12}>
                  <TextField
                    label="Notes"
                    value={item.notes}
                    onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Add any additional notes or comments..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Box>
      {/* Add Item Button */}
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          variant="outlined"
          disabled={!canAddItem}
          fullWidth
          sx={{
            py: 1.5,
            '&:hover': {
              backgroundColor: 'primary.light',
              color: 'white',
            },
          }}
        >
          Add Another Item
        </Button>
      </Box>
    </OverwatchModal>
  );
};

export default DeliveryModal;
